import {
  CheckCircleOutline,
  EditOutlined,
  HistoryOutlined,
  HourglassEmptyOutlined,
  OpenInNewOutlined,
  LocalAtmOutlined,
  PaidOutlined,
  PersonAddOutlined,
  ReceiptLongOutlined,
  RemoveCircleOutline,
  SendOutlined,
  ShieldOutlined,
} from "@mui/icons-material";
import { Box, Button, Divider, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDrilldownBaseUrl } from "../../../../pipelines/PipelineContext";
import { blue, green, grey, orange, red } from "@mui/material/colors";
import { isEmpty, isNil, size } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isConstructionProduct } from "../../../../utils/loan/productType";

import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import FlxGrid from "../../../../_src_shared/components/layout/FlxGrid";
import PrimaryContentCard from "../../../../_src_shared/components/layout/PrimaryContentCard";
import UpdateLoanRootDataFieldsModal from "../../../../components/common/Loan/UpdateLoanRootDataFieldsModal";
import SelectInput from "../../../../components/inputs/SelectInput";
import Flx from "../../../../components/layout/Flx";
import ScreenContent from "../../../../components/layout/ScreenContent";
import BasicModal from "../../../../components/modals/BasicModal";
import DetailRow from "../../../../components/sidebar/shared/DetailRow";
import SidebarSection from "../../../../components/sidebar/shared/SidebarSection";
import {
  FIELD_TYPE,
  countVisibleFields,
} from "../../../../components/sidebar/shared/sidebarFieldUtils";
import SetFundingEntityModal from "../../../../components/common/Loan/SetFundingEntityModal";
import InsetCard from "../../../../components/ui/InsetCard";
import Txt from "../../../../components/typography/Txt";
import { loanDrilldownSidebarTypes } from "../../../../constants/enums/loanDrilldownScreenSidebarOptions";
import { useLoanTermsHook } from "../../../../hooks/useLoanTermsHook";
import {
  sidebarSetStateValue,
  sidebarTypeSet,
} from "../../../../redux/actions/sidebarActions";
import {
  loanFetchedDataFundingEntitiesSet,
  loanFetchedDataLoanTermsSet,
} from "../../../../redux/reducers/loanFetchedDataReducer";
import { formatBoolean } from "../../../../utils/booleans/formatBoolean";
import { formatDollar } from "../../../../utils/numbers/formatDollar";
import { formatNumber } from "../../../../utils/numbers/formatNumber";
import { formatPercent } from "../../../../utils/numbers/formatPercent";
import LoanDrilldownTabPanel from "../LoanDrilldownTabPanel";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

const LoanDrilldownLoanTermsTab = () => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanTerms = useSelector((state) => state?.loanFetchedData?.loanTerms);
  const fundingEntities = useSelector(
    (state) => state?.loanFetchedData?.fundingEntities,
  );
  const { getLoanTerms, getFundingEntities } = useLoanTermsHook();

  useEffect(() => {
    if (isNil(loanTerms) && loanDrilldown?._id) {
      getLoanTerms({
        dealId: loanDrilldown?._id,
        onSuccessFn: (data) => dispatch(loanFetchedDataLoanTermsSet(data)),
        onFailFn: () => dispatch(loanFetchedDataLoanTermsSet({})),
      });
    }
  }, [loanTerms, loanDrilldown?._id]);

  useEffect(() => {
    if (isNil(fundingEntities)) {
      getFundingEntities({
        onSuccessFn: (data) =>
          dispatch(loanFetchedDataFundingEntitiesSet(data)),
        onFailFn: () => dispatch(loanFetchedDataFundingEntitiesSet({})),
      });
    }
  }, [fundingEntities]);

  const headerSubtitle = useMemo(() => {
    const loanNumber = loanDrilldown?.loanNumber || "XXXXXX";
    const firstPropertyAddress =
      addressLabel(loanDrilldown?.subjectProperties?.[0]?.address);
    const propertyCount = size(loanDrilldown?.subjectProperties);
    const suffix = propertyCount > 1 ? ` (+${propertyCount - 1} more)` : "";
    return firstPropertyAddress
      ? `Loan #${loanNumber} — ${firstPropertyAddress}${suffix}`
      : `Loan #${loanNumber}`;
  }, [loanDrilldown]);

  const isLoading = isNil(loanTerms);

  return (
    <>
      <LoanDrilldownTabPanel>
        <ScreenContent sx={{ width: "100%", margin: "0 auto" }}>
          <PrimaryContentCard
            maxWidth="lg"
            title="Loan Terms"
            subTitle={headerSubtitle}
            loading={isLoading}
          >
            {!isLoading && (
              <Flx column g={3}>
                <StatusTiles loanTerms={loanTerms} />
                <FundingAndTitleGrid
                  loanTerms={loanTerms}
                  fundingEntities={fundingEntities}
                />
                <FlxGrid width="340px" gap={2}>
                  <DerivedFromLoanSection loanDrilldown={loanDrilldown} />
                  <TotalsSection loanTerms={loanTerms} />
                </FlxGrid>
                <FeeCollectionsSection loanTerms={loanTerms} />
                <SpecialTermsSection loanTerms={loanTerms} />
              </Flx>
            )}
          </PrimaryContentCard>
        </ScreenContent>
      </LoanDrilldownTabPanel>
    </>
  );
};

/* ═══════════════════════════════════════════
   STATUS TILES — at-a-glance pipeline summary
   ═══════════════════════════════════════════ */

const TERMS_ACCEPTED_FIELDS = [
  { name: "termsAccepted", label: "Terms Accepted?", type: "booleanToggle" },
  { name: "termsAcceptedDate", label: "Terms Accepted Date", type: "date" },
];

const FUNDING_SHIELD_FIELDS = [
  {
    name: "fundingShieldStatus",
    label: "Funding Shield Status",
    type: "select",
    options: ["Not Submitted", "Submitted", "Received"],
  },
  {
    name: "fundingShieldRequestedDate",
    label: "Funding Shield Requested Date",
    type: "date",
  },
];

const StatusTiles = ({ loanTerms }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const navigate = useNavigate();
  const baseUrl = useDrilldownBaseUrl(loanDrilldown?._id);
  const [editingTermsAccepted, setEditingTermsAccepted] = useState(false);
  const [editingFundingShield, setEditingFundingShield] = useState(false);

  const tiles = useMemo(
    () => [
      {
        label: "Terms Accepted",
        value: formatBoolean(loanTerms?.termsAccepted, {
          yes: "Accepted",
          no: "Pending",
          fallback: "Pending",
        }),
        secondary: loanTerms?.termsAcceptedDate
          ? formatDate(loanTerms?.termsAcceptedDate)
          : null,
        icon: <CheckCircleOutline />,
        tone: loanTerms?.termsAccepted ? "success" : "muted",
        onEdit: () => setEditingTermsAccepted(true),
      },
      {
        label: "Closing",
        value: loanTerms?.closingStatus || "Not Set",
        secondary: loanTerms?.closingScheduledDate
          ? `Scheduled ${formatDate(loanTerms?.closingScheduledDate)}`
          : loanTerms?.closingScheduled
            ? "Scheduled"
            : "Unscheduled",
        icon: <HourglassEmptyOutlined />,
        tone: loanTerms?.closingScheduled ? "info" : "muted",
      },
      {
        label: "Funding Shield",
        value: loanTerms?.fundingShieldStatus || "Not Submitted",
        secondary: loanTerms?.fundingShieldRequestedDate
          ? `Requested ${formatDate(loanTerms?.fundingShieldRequestedDate)}`
          : null,
        icon: <ShieldOutlined />,
        // Straight to the surface that actually builds and files the request.
        onOpen: () =>
          navigate(`${baseUrl}/wire-and-funding/funding-shield`),
        openTitle: "Open Funding Shield on Wire & Funding",
        tone:
          loanTerms?.fundingShieldStatus === "Received"
            ? "success"
            : loanTerms?.fundingShieldStatus === "Submitted"
              ? "info"
              : "warning",
        onEdit: () => setEditingFundingShield(true),
      },
      {
        label: "Wire",
        value: loanTerms?.wireStatus || "Not Sent",
        secondary: loanTerms?.wireSentDate
          ? `Sent ${formatDate(loanTerms?.wireSentDate)}`
          : null,
        icon: <SendOutlined />,
        tone: loanTerms?.wireSentDate ? "success" : "muted",
      },
    ],
    [loanTerms, baseUrl, navigate],
  );

  return (
    <>
      <FlxGrid width="220px" gap={1.5}>
        {tiles.map((t) => (
          <StatusTile key={t.label} {...t} />
        ))}
      </FlxGrid>
      <UpdateLoanRootDataFieldsModal
        show={editingTermsAccepted}
        onClose={() => setEditingTermsAccepted(false)}
        fields={TERMS_ACCEPTED_FIELDS}
        maxWidth="xs"
      />
      <UpdateLoanRootDataFieldsModal
        show={editingFundingShield}
        onClose={() => setEditingFundingShield(false)}
        fields={FUNDING_SHIELD_FIELDS}
        maxWidth="xs"
      />
    </>
  );
};

const TONE_COLORS = {
  success: { fg: green[700], bg: green[50], border: green[100] },
  info: { fg: blue[700], bg: blue[50], border: blue[100] },
  warning: { fg: orange[700], bg: orange[50], border: orange[100] },
  error: { fg: red[700], bg: red[50], border: red[100] },
  muted: { fg: grey[700], bg: grey[50], border: grey[200] },
};

const StatusTile = ({
  label,
  value,
  secondary,
  icon,
  tone = "muted",
  onEdit,
  onOpen,
  openTitle,
}) => {
  const colors = TONE_COLORS[tone] || TONE_COLORS.muted;
  return (
    <Box
      sx={{
        position: "relative",
        border: `1px solid ${colors.border}`,
        background: colors.bg,
        borderRadius: 2,
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.4,
        minHeight: 78,
      }}
    >
      <Flx ac jb sx={{ color: colors.fg }}>
        <Flx ac g={0.75} sx={{ "& svg": { fontSize: 15 } }}>
          {icon}
          <Txt sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4 }}>
            {String(label).toUpperCase()}
          </Txt>
        </Flx>
        <Flx ac g={0.25}>
          {onOpen && (
            <Tooltip title={openTitle || `Open ${label}`}>
              <IconButton
                size="small"
                onClick={onOpen}
                sx={{ p: 0.25, color: colors.fg }}
              >
                <OpenInNewOutlined sx={{ fontSize: 14 }} className="thin" />
              </IconButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title={`Edit ${label}`}>
              <IconButton
                size="small"
                onClick={onEdit}
                sx={{ p: 0.25, color: colors.fg }}
              >
                <EditOutlined sx={{ fontSize: 14 }} className="thin" />
              </IconButton>
            </Tooltip>
          )}
        </Flx>
      </Flx>
      <Txt sx={{ fontSize: 14, fontWeight: 600, color: "#1e293b" }}>
        {value}
      </Txt>
      {secondary && (
        <Txt sx={{ fontSize: 11, color: grey[600] }}>{secondary}</Txt>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════
   FUNDING ENTITY + TITLE CONTACT
   ═══════════════════════════════════════════ */

const FundingAndTitleGrid = ({ loanTerms, fundingEntities }) => {
  const dispatch = useDispatch();
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  // updateFundingEntity is no longer destructured here — SetFundingEntityModal
  // owns that write now, so both entry points share one path.
  const { loading, updateTitleContact } = useLoanTermsHook();

  const [editingFundingEntity, setEditingFundingEntity] = useState(false);
  const [editingTitleContact, setEditingTitleContact] = useState(false);

  const fundingEntity = loanTerms?.fundingEntity;
  const selectedEntity = useMemo(() => {
    if (!fundingEntity?._id) return fundingEntity;
    return (
      fundingEntities?.find?.((e) => e?._id === fundingEntity?._id) ||
      fundingEntity
    );
  }, [fundingEntity, fundingEntities]);

  const titleContact = loanTerms?.titleContact;
  const hasTitleContact = !isNil(titleContact) && !isEmpty(titleContact);

  // The funding-entity write now lives inside SetFundingEntityModal, so the two
  // surfaces that can set it (here and the Wire & Funding gate) share one path.

  const handleSelectTitleContact = useCallback(
    (contactId) => {
      const contactData = loanDrilldown?.loanRelatedContacts?.find(
        (c) => c?._id === contactId,
      );
      if (isNil(contactData)) return;
      updateTitleContact({
        dealId: loanDrilldown?._id,
        data: contactData,
        onSuccessFn: (data) => {
          dispatch(loanFetchedDataLoanTermsSet(data));
          setEditingTitleContact(false);
        },
      });
    },
    [loanDrilldown, dispatch, updateTitleContact],
  );

  const handleUnassignTitleContact = useCallback(() => {
    updateTitleContact({
      dealId: loanDrilldown?._id,
      data: null,
      onSuccessFn: (data) => {
        dispatch(loanFetchedDataLoanTermsSet(data));
        setEditingTitleContact(false);
      },
    });
  }, [loanDrilldown?._id, dispatch, updateTitleContact]);

  const handleAddNewTitleContact = useCallback(() => {
    setEditingTitleContact(false);
    dispatch(
      sidebarTypeSet(loanDrilldownSidebarTypes.ADD_LOAN_RELATED_CONTACT),
    );
    // Prefill the role — this add flow is for the title contact, so default the
    // new contact's role to "Title Agent" (the closer can still change it).
    dispatch(sidebarSetStateValue({ loanRole: "Title Agent" }));
  }, [dispatch]);

  return (
    <>
      <FlxGrid width="360px" gap={2}>
        <InsetCard grey sx={{ p: 2.5, position: "relative" }}>
          <Flx column g={1}>
            <Flx ac jb>
              <SectionLabel>Funding Entity</SectionLabel>
              <Tooltip
                title={
                  isNil(selectedEntity)
                    ? "Set funding entity"
                    : "Change funding entity"
                }
              >
                <IconButton
                  size="small"
                  onClick={() => setEditingFundingEntity(true)}
                  sx={{ p: 0.25 }}
                >
                  <EditOutlined sx={{ fontSize: 15 }} className="thin" />
                </IconButton>
              </Tooltip>
            </Flx>
            {isNil(selectedEntity) ? (
              <EmptyAssignmentBlock
                message="No funding entity assigned."
                actionLabel="Set Funding Entity"
                onAction={() => setEditingFundingEntity(true)}
              />
            ) : (
              <MortgageeClause entity={selectedEntity} />
            )}
          </Flx>
        </InsetCard>

        <InsetCard grey sx={{ p: 2.5, position: "relative" }}>
          <Flx column g={1}>
            <Flx ac jb>
              <SectionLabel>Title Contact</SectionLabel>
              <Flx ac g={0.25}>
                {hasTitleContact && (
                  <Tooltip title="Unassign title contact">
                    <IconButton
                      size="small"
                      onClick={handleUnassignTitleContact}
                      disabled={loading}
                      sx={{ p: 0.25 }}
                    >
                      <RemoveCircleOutline
                        sx={{ fontSize: 15 }}
                        className="thin"
                      />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip
                  title={
                    hasTitleContact
                      ? "Change title contact"
                      : "Assign title contact"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={() => setEditingTitleContact(true)}
                    sx={{ p: 0.25 }}
                  >
                    <EditOutlined sx={{ fontSize: 15 }} className="thin" />
                  </IconButton>
                </Tooltip>
              </Flx>
            </Flx>
            {hasTitleContact ? (
              <TitleContactBlock contact={titleContact} />
            ) : (
              <EmptyAssignmentBlock
                message="No title contact assigned."
                actionLabel="Assign Title Contact"
                onAction={() => setEditingTitleContact(true)}
              />
            )}
          </Flx>
        </InsetCard>
      </FlxGrid>

      {/* Shared with the Wire & Funding tab's funding-entity gate — one dialog,
          one write path, so the two entry points can't drift. */}
      <SetFundingEntityModal
        show={editingFundingEntity}
        onClose={() => setEditingFundingEntity(false)}
        dealId={loanDrilldown?._id}
        currentEntityId={selectedEntity?._id}
      />

      <SelectTitleContactModal
        show={editingTitleContact}
        onClose={() => setEditingTitleContact(false)}
        loanRelatedContacts={loanDrilldown?.loanRelatedContacts}
        currentContactId={titleContact?._id}
        onSelect={handleSelectTitleContact}
        onAddNew={handleAddNewTitleContact}
        onUnassign={hasTitleContact ? handleUnassignTitleContact : null}
        loading={loading}
      />
    </>
  );
};

const EmptyAssignmentBlock = ({ message, actionLabel, onAction }) => (
  <Box
    sx={{
      background: "#ffffff",
      border: `1px dashed ${grey[300]}`,
      borderRadius: 1.5,
      p: 1.5,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: 1,
    }}
  >
    <Txt secondary sx={{ fontSize: 12 }}>
      {message}
    </Txt>
    <Button
      size="small"
      variant="outlined"
      onClick={onAction}
      sx={{ fontSize: 11 }}
    >
      {actionLabel}
    </Button>
  </Box>
);

const SelectTitleContactModal = ({
  show,
  onClose,
  loanRelatedContacts,
  currentContactId,
  onSelect,
  onAddNew,
  onUnassign,
  loading,
}) => {
  const [pending, setPending] = useState(currentContactId || "");

  useEffect(() => {
    if (show) setPending(currentContactId || "");
  }, [show, currentContactId]);

  const options = useMemo(
    () =>
      (loanRelatedContacts || []).map((c) => {
        const name = `${c?.firstName || ""} ${c?.lastName || ""}`.trim();
        // Show the contact's role next to their name so the closer can tell
        // e.g. the Title Agent from the Escrow Officer at a glance.
        const role = c?.loanRole ? ` (${c.loanRole})` : "";
        const email = c?.emailAddress || "Unknown Email";
        return {
          value: c?._id,
          label: `${name}${role} <${email}>`.trim(),
        };
      }),
    [loanRelatedContacts],
  );

  const noContacts = isEmpty(options);

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Set Title Contact"
      maxWidth="xs"
      icon={<EditOutlined className="thin" />}
    >
      <Flx column g={1.75}>
        {noContacts ? (
          <>
            <Txt sx={{ fontSize: 13, color: grey[700] }}>
              No loan-related contacts available — add one to assign as the
              title contact.
            </Txt>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PersonAddOutlined className="thin" />}
              onClick={onAddNew}
              sx={{
                borderStyle: "dashed",
                py: 1.2,
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Add new contact
            </Button>
          </>
        ) : (
          <>
            <Txt sx={{ fontSize: 13, color: grey[700] }}>
              Choose a loan-related contact to act as the title contact.
            </Txt>
            <SelectInput
              value={pending}
              onChange={(v) => setPending(v)}
              options={options}
              placeholder="Select title contact"
              fullWidth
            />
            <Button
              size="small"
              variant="text"
              startIcon={<PersonAddOutlined className="thin" />}
              onClick={onAddNew}
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Add new contact
            </Button>
          </>
        )}
        <Flx jb ac sx={{ mt: 0.5 }}>
          <Flx g={1}>
            <Button onClick={onClose} variant="text" disabled={loading}>
              Cancel
            </Button>
            {onUnassign && (
              <Button
                onClick={onUnassign}
                variant="text"
                color="error"
                disabled={loading}
                startIcon={<RemoveCircleOutline className="thin" />}
              >
                Unassign
              </Button>
            )}
          </Flx>
          {!noContacts && (
            <Button
              variant="contained"
              disabled={!pending || pending === currentContactId || loading}
              onClick={() => onSelect(pending)}
            >
              Save
            </Button>
          )}
        </Flx>
      </Flx>
    </BasicModal>
  );
};

const SectionLabel = ({ children }) => (
  <Txt
    sx={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, color: grey[700] }}
  >
    {String(children).toUpperCase()}
  </Txt>
);

const MortgageeClause = ({ entity }) => {
  const cityState = [entity?.city, entity?.state].filter(Boolean).join(", ");
  const addressLine = [cityState, entity?.zipCode].filter(Boolean).join(" ");
  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[200]}`,
        borderRadius: 1.5,
        p: 1.5,
        fontFamily: "monospace",
        fontSize: 12,
        lineHeight: "20px",
        color: "#1e293b",
        whiteSpace: "pre-line",
      }}
    >
      <Box sx={{ fontWeight: 700 }}>{entity?.name} ISAOA/ATIMA</Box>
      {entity?.addressLine1 && <div>{entity.addressLine1}</div>}
      {entity?.addressLine2 && <div>{entity.addressLine2}</div>}
      {addressLine && <div>{addressLine}</div>}
    </Box>
  );
};

const TitleContactBlock = ({ contact }) => {
  if (isNil(contact) || isEmpty(contact)) {
    return (
      <Txt secondary sx={{ fontSize: 12 }}>
        No title contact assigned.
      </Txt>
    );
  }
  const fullName = [contact?.firstName, contact?.lastName]
    .filter(Boolean)
    .join(" ");
  return (
    <Box
      sx={{
        background: "#ffffff",
        border: `1px solid ${grey[200]}`,
        borderRadius: 1.5,
        p: 1.5,
        fontSize: 12,
        lineHeight: "20px",
      }}
    >
      <Box sx={{ fontWeight: 600, fontSize: 13 }}>
        {fullName || "Unnamed Contact"}
      </Box>
      {contact?.companyName && <div>{contact.companyName}</div>}
      {contact?.emailAddress && (
        <div style={{ color: grey[700] }}>{contact.emailAddress}</div>
      )}
      {contact?.phoneNumber && (
        <div style={{ color: grey[700] }}>{contact.phoneNumber}</div>
      )}
    </Box>
  );
};

/* ═══════════════════════════════════════════
   DERIVED-FROM-LOAN + TOTALS
   ═══════════════════════════════════════════ */

const DerivedFromLoanSection = ({ loanDrilldown }) => {
  const fields = useMemo(
    () => [
      {
        label: "Interest Rate",
        value: formatPercent(loanDrilldown?.interestRate),
        fieldType: FIELD_TYPE.COMPUTED,
        computedTooltip: "Sourced from underwriting",
      },
      {
        label: "Interest Calc Type",
        value: loanDrilldown?.interestCalcType,
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Dutch Interest",
        value: formatBoolean(loanDrilldown?.dutchInterest),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Origination Points",
        value: formatPercent(loanDrilldown?.originationPoints),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Discount Points",
        value: formatPercent(loanDrilldown?.discountPoints),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Term (months)",
        value: formatNumber(loanDrilldown?.termMonths),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "First Payment Date",
        value: formatDate(loanDrilldown?.firstPaymentDate),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Maturity Date",
        value: formatDate(loanDrilldown?.maturityDate),
        fieldType: FIELD_TYPE.COMPUTED,
      },
    ],
    [loanDrilldown],
  );

  return <FieldsCard title="Derived From Loan" fields={fields} />;
};

const TotalsSection = ({ loanTerms }) => {
  // Interest reserves + holdback are construction economics — hidden for DSCR.
  const construction = isConstructionProduct(
    useSelector((state) => state.loanDrilldown?.loanProductType),
  );
  const fields = useMemo(
    () => [
      {
        label: "Total Loan Amount",
        value: formatDollar(loanTerms?.totalLoanAmount),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Originator Fees",
        value: formatDollar(loanTerms?.totalOriginatorFees),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Originator Credits",
        value: formatDollar(loanTerms?.totalOriginatorCredits),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Third Party Fees",
        value: formatDollar(loanTerms?.totalThirdPartyFees),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Correspondent Fees",
        value: formatDollar(loanTerms?.totalCorrespondentFees),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Broker Terms",
        value: formatDollar(loanTerms?.totalBrokerTerms),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      {
        label: "Escrow Items",
        value: formatDollar(loanTerms?.totalEscrowItems),
        fieldType: FIELD_TYPE.COMPUTED,
      },
      ...(construction
        ? [
            {
              label: "Interest Reserves",
              value: formatDollar(loanTerms?.totalInterestReserves),
              fieldType: FIELD_TYPE.COMPUTED,
            },
            {
              label: "Capitalized (Financed) Interest Reserve",
              value: formatDollar(loanTerms?.capitalizedInterestReserve),
              fieldType: FIELD_TYPE.COMPUTED,
            },
            {
              label: "Holdback",
              value: formatDollar(loanTerms?.totalHoldback),
              fieldType: FIELD_TYPE.COMPUTED,
            },
          ]
        : []),
    ],
    [loanTerms, construction],
  );

  return <FieldsCard title="Totals" fields={fields} />;
};

const FieldsCard = ({ title, fields, onEdit }) => {
  const { visible, total } = useMemo(
    () => countVisibleFields(fields),
    [fields],
  );
  return (
    <Box
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: 2,
        background: "#ffffff",
        px: 2,
      }}
    >
      <SidebarSection
        title={title}
        onEdit={onEdit}
        visibleCount={visible}
        totalCount={total}
        underlinePosition="title"
      >
        {fields.map((f) => (
          <DetailRow
            key={f.label}
            label={f.label}
            value={f.value}
            editable={f.fieldType === FIELD_TYPE.EDITABLE}
            computed={f.fieldType === FIELD_TYPE.COMPUTED}
            computedTooltip={f.computedTooltip}
          />
        ))}
      </SidebarSection>
    </Box>
  );
};

/* ═══════════════════════════════════════════
   FEE COLLECTIONS — summary of list objects
   ═══════════════════════════════════════════ */

const FEE_COLLECTIONS = [
  { key: "originatorFees", label: "Originator Fees", icon: <PaidOutlined /> },
  {
    key: "originatorCredits",
    label: "Originator Credits",
    icon: <LocalAtmOutlined />,
  },
  {
    key: "thirdPartyFees",
    label: "Third-Party Fees",
    icon: <ReceiptLongOutlined />,
  },
  {
    key: "correspondentFees",
    label: "Correspondent Fees",
    icon: <ReceiptLongOutlined />,
  },
  { key: "brokerTerms", label: "Broker Terms", icon: <PaidOutlined /> },
  {
    key: "escrowItems",
    label: "Escrow Items",
    icon: <ReceiptLongOutlined />,
  },
];

const FeeCollectionsSection = ({ loanTerms }) => {
  return (
    <Flx column g={1}>
      <SectionLabel>Fee & Credit Summary</SectionLabel>
      <FlxGrid width="220px" gap={1.5}>
        {FEE_COLLECTIONS.map(({ key, label, icon }) => {
          const items = loanTerms?.[key] || [];
          const total = items.reduce(
            (acc, item) => acc + (Number(item?.totalDueAmount) || 0),
            0,
          );
          return (
            <FeeSummaryTile
              key={key}
              label={label}
              count={items.length}
              total={total}
              icon={icon}
            />
          );
        })}
      </FlxGrid>
    </Flx>
  );
};

const FeeSummaryTile = ({ label, count, total, icon }) => {
  return (
    <Box
      sx={{
        border: `1px solid ${grey[200]}`,
        borderRadius: 2,
        background: "#ffffff",
        p: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 0.5,
      }}
    >
      <Flx ac g={0.75} sx={{ color: grey[600], "& svg": { fontSize: 15 } }}>
        {icon}
        <Txt sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.4 }}>
          {String(label).toUpperCase()}
        </Txt>
      </Flx>
      <Flx ac jb>
        <Txt sx={{ fontSize: 18, fontWeight: 600, color: "#1e293b" }}>
          {formatDollar(total)}
        </Txt>
        <Txt sx={{ fontSize: 11, color: grey[600] }}>
          {count} {count === 1 ? "item" : "items"}
        </Txt>
      </Flx>
    </Box>
  );
};

/* ═══════════════════════════════════════════
   SPECIAL TERMS
   ═══════════════════════════════════════════ */

const SpecialTermsSection = ({ loanTerms }) => {
  const textBasedTerms = loanTerms?.textBasedTerms || [];
  if (isEmpty(textBasedTerms)) return null;

  return (
    <Flx column g={1}>
      <SectionLabel>Special Terms & Conditions</SectionLabel>
      <Flx column g={1}>
        {textBasedTerms.map((t) => (
          <InsetCard key={t._id || t.id} sx={{ p: 2 }}>
            <Flx ac jb sx={{ mb: 1 }}>
              <Txt sx={{ fontSize: 11, color: grey[600] }}>
                {formatDate(t.createdDate)}
              </Txt>
            </Flx>
            <Divider sx={{ mb: 1 }} />
            <Txt
              sx={{
                whiteSpace: "pre-line",
                fontSize: 12,
                color: "#1e293b",
              }}
            >
              {t.termValue || "No details provided."}
            </Txt>
          </InsetCard>
        ))}
      </Flx>
    </Flx>
  );
};

export default LoanDrilldownLoanTermsTab;
